import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import { AdminAuthProvider } from "@/lib/admin-auth";
import { SizingProvider } from "@/lib/sizing-store";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Analytics from "@/pages/Analytics";
import WidgetSizingPanel from "@/pages/WidgetSizingPanel";
import SizeChartModal from "@/pages/SizeChartModal";
import FindMySizeWizard from "@/pages/FindMySizeWizard";
import Sizes from "@/pages/Sizes";
import MeasurementGroups from "@/pages/MeasurementGroups";
import Models from "@/pages/Models";
import { Products, Tables } from "@/pages/ProductsTables";
import FitInfoLibrary from "@/pages/FitInfoLibrary";
import ImagesLibrary from "@/pages/ImagesLibrary";
import Settings from "@/pages/Settings";
import Integration from "@/pages/Integration";
// Admin
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTenantDetail from "@/pages/admin/AdminTenantDetail";
import AdminChangePassword from "@/pages/admin/AdminChangePassword";
import { ADMIN_PATH } from "@/lib/admin-path";
import LiveEmbed from "@/pages/LiveEmbed";
// Legal
import KVKK from "@/pages/legal/KVKK";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Cookies from "@/pages/legal/Cookies";
import CookieBanner from "@/components/legal/CookieBanner";

export default function App() {
    return (
        <I18nProvider>
            <BrowserRouter>
                <AdminAuthProvider>
                    <AuthProvider>
                        <SizingProvider>
                            {/* Header height = h-16 (64px). Offset 80px places toasts */}
                            {/* fully below the header's border-bottom with breathing room. */}
                            <Toaster
                                richColors
                                position="top-right"
                                offset={80}
                                toastOptions={{ style: { marginRight: 8 } }}
                            />
                            <Routes>
                                {/* Public */}
                                <Route path="/" element={<Login />} />

                                {/* Live Embed — public preview routes consumed by widget.js iframes.
                                    Same React preview components as the panel = guaranteed parity. */}
                                <Route path="/live-embed/:surface/:siteKey/:sku" element={<LiveEmbed />} />

                                {/* Legal — public, no auth required */}
                                <Route path="/legal/kvkk" element={<KVKK />} />
                                <Route path="/legal/privacy" element={<Privacy />} />
                                <Route path="/legal/terms" element={<Terms />} />
                                <Route path="/legal/cookies" element={<Cookies />} />

                                {/* Admin (separate scope, path from env — tahmin edilmesin diye `/admin` değil)
                                    NOT: Login ve Shell AYNI path'i paylaşmamalı. Login = ADMIN_PATH,
                                    protected sayfalar = ADMIN_PATH/<sub>. Aksi halde Shell'in index
                                    redirect'i ile Login arasında sonsuz navigasyon döngüsü oluşur. */}
                                <Route path={ADMIN_PATH} element={<AdminLogin />} />
                                <Route path={`${ADMIN_PATH}/change-password`} element={<AdminChangePassword />} />
                                <Route path={ADMIN_PATH} element={<AdminShell />}>
                                    <Route path="overview" element={<AdminDashboard />} />
                                    <Route path="tenants/:id" element={<AdminTenantDetail />} />
                                </Route>

                                {/* Legacy /admin ve /sayz-super-admin → yeni path'e kalıcı yönlendirme */}
                                <Route path="/admin" element={<Navigate to={ADMIN_PATH} replace />} />
                                <Route path="/admin/*" element={<Navigate to={ADMIN_PATH} replace />} />
                                <Route path="/sayz-super-admin" element={<Navigate to={ADMIN_PATH} replace />} />
                                <Route path="/sayz-super-admin/*" element={<Navigate to={ADMIN_PATH} replace />} />

                                {/* Tenant app (protected) */}
                                <Route path="/app" element={
                                    <ProtectedRoute>
                                        <AppShell />
                                    </ProtectedRoute>
                                }>
                                    <Route index element={<Dashboard />} />
                                    <Route path="analytics" element={<Analytics />} />
                                    <Route path="widgets/panel" element={<WidgetSizingPanel />} />
                                    <Route path="experiences/size-chart" element={<SizeChartModal />} />
                                    <Route path="experiences/find-my-size" element={<FindMySizeWizard />} />
                                    <Route path="sizing/sizes" element={<Sizes />} />
                                    <Route path="sizing/measurement-groups" element={<MeasurementGroups />} />
                                    <Route path="sizing/models" element={<Models />} />
                                    <Route path="sizing/fit-info" element={<FitInfoLibrary />} />
                                    <Route path="sizing/images" element={<ImagesLibrary />} />
                                    {/* KATALOG — yeni ana grup */}
                                    <Route path="catalog/products" element={<Products />} />
                                    <Route path="catalog/tables" element={<Tables />} />
                                    {/* Legacy redirect — /sizing/products → /catalog/products */}
                                    <Route path="sizing/products" element={<Navigate to="/app/catalog/products" replace />} />
                                    <Route path="settings" element={<Settings />} />
                                    <Route path="integration" element={<Integration />} />
                                </Route>

                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </SizingProvider>
                    </AuthProvider>
                </AdminAuthProvider>
            </BrowserRouter>
        </I18nProvider>
    );
}
